// src/services/functionsService.js
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { logger } from "../utils/logger";

/**
 * @file This service provides functions for calling Firebase Cloud Functions.
 */

// Helper to measure performance of async operations
const measureFunctionsPerformance = async (operationName, promiseFn) => {
  const start = performance.now();
  try {
    const result = await promiseFn();
    const end = performance.now();
    logger.log(`functionsService: ${operationName} completed in ${(end - start).toFixed(2)} ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    logger.error(`functionsService: ${operationName} failed in ${(end - start).toFixed(2)} ms`, error);
    throw error;
  }
};

/**
 * Saves the user's login preferences.
 *
 * @param {object} data - The data to save.
 * @param {string} data.uid - The user's ID.
 * @param {boolean} data.keepLoggedIn - A flag indicating if the user should be kept logged in.
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const saveUserLoginPreferences = (data) => {
  logger.log("functionsService: Calling saveUserLoginPreferences", data);
  const func = httpsCallable(functions, "saveUserLoginPreferences");
  return measureFunctionsPerformance("saveUserLoginPreferences", () => func(data));
};

/**
 * Saves the user on signup.
 *
 * @param {object} data - The data to save.
 * @param {string} data.email - The user's email.
 * @param {string} data.uid - The user's ID.
 * @param {boolean} [data.keepLoggedIn=false] - A flag indicating if the user should be kept logged in.
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const saveUserOnSignup = (data) => {
  logger.log("functionsService: Calling saveUserOnSignup", data);
  const func = httpsCallable(functions, "saveUserOnSignup");
  return measureFunctionsPerformance("saveUserOnSignup", () => func(data));
};

/**
 * Gets the user's favorite team.
 *
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const getUserFavoriteTeam = () => {
  logger.log("functionsService: Calling getUserFavoriteTeam");
  const func = httpsCallable(functions, "getUserFavoriteTeam");
  return measureFunctionsPerformance("getUserFavoriteTeam", () => func());
};

/**
 * Saves the user's favorite team.
 *
 * @param {object} data - The data to save.
 * @param {string} data.teamId - The ID of the team to set as favorite.
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const saveUserFavoriteTeam = (data) => {
  logger.log("functionsService: Calling saveUserFavoriteTeam", data);
  const func = httpsCallable(functions, "saveUserFavoriteTeam");
  return measureFunctionsPerformance("saveUserFavoriteTeam", () => func(data));
};

/**
 * Deletes the user's account.
 *
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const deleteUserAccount = () => {
  logger.log("functionsService: Calling deleteUserAccount");
  const func = httpsCallable(functions, "deleteUserAccount");
  return measureFunctionsPerformance("deleteUserAccount", () => func());
};

/**
 * Handles the transition of beta users to trialing status when plans are enabled.
 *
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const handleBetaUserTransition = () => {
  logger.log("functionsService: Calling handleBetaUserTransition");
  const func = httpsCallable(functions, "handleBetaUserTransition");
  return measureFunctionsPerformance("handleBetaUserTransition", () => func());
};

/**
 * Updates the user's preferences, including their plan and favorite team.
 *
 * @param {object} data - The data to save.
 * @param {string} data.planId - The ID of the selected plan.
 * @param {string} data.teamId - The ID of the selected team.
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const updateUserPreferences = (data) => {
  logger.log("functionsService: Calling updateUserPreferences", data);
  const func = httpsCallable(functions, "updateUserPreferences");
  return measureFunctionsPerformance("updateUserPreferences", () => func(data));
};

/**
 * Gets the league standings.
 *
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const getLeagueStandings = () => {
  logger.log("functionsService: Calling getLeagueStandings");
  const func = httpsCallable(functions, "getLeagueStandings");
  return measureFunctionsPerformance("getLeagueStandings", () => func());
};

/**
 * Fetches player statistics for a specific fixture.
 *
 * @param {object} data - The data for the request.
 * @param {string} data.fixtureId - The ID of the fixture to fetch player stats for.
 * @returns {Promise<HttpsCallableResult>} A promise that resolves with the result of the function call.
 */
export const adminFetchPlayerStats = (data) => {
  logger.log("functionsService: Calling adminFetchPlayerStats", data);
  const func = httpsCallable(functions, "adminFetchPlayerStats");
  return measureFunctionsPerformance("adminFetchPlayerStats", () => func(data));
};
